import React, { useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Avatar,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import { LocalShipping, Business } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/AddOutlined";
import ForwardAddressDialog from "components/ForwardAddressDialog";

const ForwardAddressList = ({
  forwardAddresses,
  setForwardAddresses,
  isForwardRequired,
}) => {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editAddress, setEditAddress] = useState(null);

  const openAdd = () => { setEditAddress(null); setDialogOpen(true); };
  const openEdit = (address) => { setEditAddress(address); setDialogOpen(true); };
  const closeDialog = () => { setDialogOpen(false); setEditAddress(null); };

  const handleSaveForwardAddress = (saved) => {
    if (editAddress) {
      setForwardAddresses((prev) =>
        prev.map((a) => {
          if (a.id === saved.id) return saved;
          return saved.isDefault ? { ...a, isDefault: false } : a;
        })
      );
    } else {
      if (forwardAddresses.length >= 5) {
        alert("You can only add up to 5 forward addresses.");
        return;
      }
      setForwardAddresses((prev) => {
        const reset = prev.map((a) => ({ ...a, isDefault: false }));
        return [saved, ...reset];
      });
    }
  };

  const handleRemove = (id) => {
    setForwardAddresses((prev) => {
      const filtered = prev.filter((a) => a.id !== id);
      if (filtered.length > 0 && prev.find((a) => a.id === id)?.isDefault) {
        filtered[0].isDefault = true;
      }
      return filtered;
    });
  };

  const handleToggleDefault = (target) => {
    if (forwardAddresses.length === 1 && forwardAddresses[0].isDefault) return;
    setForwardAddresses((prev) =>
      prev.map((a) => ({
        ...a,
        isDefault: a.id === target.id ? !target.isDefault : false,
      }))
    );
  };

  const accentColor = theme.palette.warning.main;
  const mutedColor = theme.palette.grey[400];

  return (
    <>
      {isForwardRequired && (
        <Box mb={2}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ borderRadius: 2, px: 2 }}
          >
            Add Address
          </Button>
          <ForwardAddressDialog
            open={dialogOpen}
            onClose={closeDialog}
            onSave={handleSaveForwardAddress}
            initialData={editAddress}
          />
        </Box>
      )}

      {(!forwardAddresses || forwardAddresses.length === 0) ? (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            border: `1.5px dashed ${theme.palette.divider}`,
            borderRadius: 3,
          }}
        >
          <LocalShipping sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No forward addresses yet
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {forwardAddresses.map((address, index) => {
            const color = address.isDefault ? accentColor : mutedColor;
            const bgColor = address.isDefault
              ? `${accentColor}18`
              : theme.palette.action.hover;

            return (
              <Grid item xs={12} sm={6} key={address.id || index}>
                <Card
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${address.isDefault ? accentColor + "50" : theme.palette.divider}`,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.13)",
                    },
                  }}
                >
                  {/* Color strip */}
                  <Box sx={{ height: 4, background: color, flexShrink: 0 }} />

                  <CardContent sx={{ p: 2, pb: "8px !important", flexGrow: 1 }}>
                    {/* Header row */}
                    <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: bgColor,
                          border: `2px solid ${color}45`,
                          flexShrink: 0,
                        }}
                      >
                        <LocalShipping sx={{ fontSize: 18, color }} />
                      </Avatar>
                      <Box minWidth={0}>
                        <Typography fontWeight={700} fontSize="0.88rem" noWrap>
                          {address.logisticCompany || `Forward Address ${index + 1}`}
                        </Typography>
                        {address.isDefault && (
                          <Chip
                            label="Default"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              bgcolor: bgColor,
                              color,
                              border: `1px solid ${color}40`,
                              fontWeight: 700,
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Branch */}
                    {address.branch && (
                      <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                        <Business sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {address.branch}
                        </Typography>
                      </Box>
                    )}

                    {/* Description */}
                    {address.description && (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.4,
                        }}
                      >
                        {address.description}
                      </Typography>
                    )}
                  </CardContent>

                  <CardActions sx={{ px: 2, py: 1, pt: 0, justifyContent: "flex-end", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleDefault(address)}
                      disabled={forwardAddresses.length === 1 && address.isDefault}
                      sx={{ color: address.isDefault ? accentColor : "text.disabled" }}
                      title={address.isDefault ? "Unset default" : "Set as default"}
                    >
                      {address.isDefault ? <ToggleOnIcon /> : <ToggleOffIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => openEdit(address)}
                      sx={{ color: theme.palette.info?.main || "#1976d2" }}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(address.id)}
                      sx={{ color: theme.palette.error.main }}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </>
  );
};

export default ForwardAddressList;
